import { useCallback, useEffect } from 'react'
import { type ExperimentType, type ExperimentValue } from '@shared/types/experiments'
import { useMutationTrackExperiment } from '@/features/shared/services/Experiments/mutations'
import { getExperimentStatus } from '@/features/shared/services/Experiments/utils'
import { getIsConsentedToAnalytics } from '@/lib/transcend/utils'
import { useExperimentsContext } from '@/features/shared/context/ExperimentsContext'
import { type QueryStatus } from '@tanstack/react-query'

/**
 * Hooks for use with Aristotle's experiment data
 */

type UseExperiment = {
  /**
   * Call `track()` whenever the member is shown the control or variation, not
   * when they interact with the experiment variation.
   */
  track: () => void

  /**
   * When active is true, show member the experiment variation. When active is
   * false, show member the experiment control.
   */
  active: boolean

  /***
   * The status of the experiments query: 'pending' | 'error' | 'success'
   */
  status: QueryStatus

  /**
   * When active is true value provides whether the user is in 'control', 'variant', 'variant_1', 'variant_2' etc.
   */
  value?: ExperimentValue

  /**
   * Call `track()` automatically if `autoTrack` is true
   */
  autoTrack?: boolean
}

const trackedExperimentsSet = new Set<string>()

export const clearTrackedExperiments = () => trackedExperimentsSet.clear()

export const useExperiment = (key: ExperimentType, { autoTrack = false } = {}): UseExperiment => {
  const { data: experiments, status } = useExperimentsContext()
  const { value, active } = getExperimentStatus(experiments, key)
  const { mutate: trackExperiment } = useMutationTrackExperiment(key)

  const track = useCallback(() => {
    // re-create this hook once the data from aristotle is loaded
    if (!value) return

    if (trackedExperimentsSet.has(key)) return
    if (!getIsConsentedToAnalytics()) return

    // add to the set immediately to prevent duplicated track calls
    trackedExperimentsSet.add(key)
    trackExperiment(
      { value },
      {
        // if track fails, remove the key from the set so we can try again later
        onError: () => trackedExperimentsSet.delete(key),
      },
    )
  }, [value, key, trackExperiment])

  useEffect(() => {
    if (autoTrack) track()
  }, [autoTrack, track])

  return {
    active,
    track,
    value,
    status,
  }
}

export const useMultiVariantExperiment = (
  experimentName: ExperimentType,
  { autoTrack = false } = {},
) => {
  const { active, track, value, status } = useExperiment(experimentName, {
    autoTrack,
  })

  const variant1 = value === 'variation_1'
  const variant2 = value === 'variation_2'
  const variant3 = value === 'variation_3'

  return {
    active,
    variant1,
    variant2,
    variant3,
    track,
    status,
  }
}
